import React, { useState, useEffect } from "react";
import { IProps } from "./types";
import C from "@assets/themes/colors";
import {
  ITextAttributes,
  ITextPartAttributes,
  ITextStyle,
  ITextPartRenderingAttributes,
} from "../../../types";
import { Group, Rect, Transformer, Shape } from "react-konva";
import Konva from "konva";
import { IResizeValues } from "../../CommonCommandsOptions/ResizeCommandOption/types";
import { defineZIndex } from "@utils/zIndexChains";
import CornersAddIn from "../AddIns/CornersAddIn";
import TextSegmentElement from "./TextSegmentElement";
import ElementLockedAddIn from "../AddIns/ElementLockedAddIn";
import { updateSelectorIcon } from "@utils/selector";
import colors from "@assets/themes/colors";

const TextCanvasElement: React.FC<IProps> = (props) => {
  let attributes = props.element.attributes as ITextAttributes;
  const textRef = React.useRef() as React.MutableRefObject<Konva.Group>;
  const trRef = React.useRef() as React.MutableRefObject<Konva.Transformer>;
  const rectRef = React.useRef() as React.MutableRefObject<Konva.Rect>;
  const lockIconRef = React.useRef() as React.MutableRefObject<Konva.Image>;

  const showDescribedColor: boolean =
    props.describedLayerColor !== "" && !attributes.hidden;
  let visible = !attributes.hidden;
  if (props.isEditing && props.isSelected) visible = false;

  useEffect(() => {
    if (props.isSelected || props.isOver || showDescribedColor) {
      trRef.current?.nodes([textRef.current]);
      trRef.current?.getLayer()?.batchDraw();
      props.showTransfomerOutside(trRef, textRef, props.isSelected);
    }
  }, [
    props.isSelected,
    props.isOver,
    props.element.attributes,
    props.describedLayerColor,
    props.zoom,
  ]);

  useEffect(() => {
    if (lockIconRef.current) lockIconRef.current.visible(false);
    if (
      props.element?.attributes &&
      (props.isSelected || props.isOver || showDescribedColor)
    )
      updateSelectorIcon(
        trRef.current,
        lockIconRef.current,
        attributes.locked === true &&
          !props.isSelectDescribe &&
          !props.isMultipleSelection
      );
  }, [
    trRef.current,
    lockIconRef.current,
    props.isOver,
    attributes.locked,
    props.isSelectDescribe,
    props.isMultipleSelection,
  ]);

  useEffect(() => {
    defineZIndex(textRef, props.element.id, props.zIndexChain);
  }, [JSON.stringify(props.zIndexChain)]);


  const getTransformerBorderColor = () => {
    if (props.isMultipleSelection && props.isOver) return colors.ORANGE;
    else
    return  showDescribedColor && !(props.isSelected || props.isOver) ? props.describedLayerColor: undefined
  };

  return (
    <>
      <Group
        name={`${props.containerId}-object`}
        key={props.element.id + "-group"}
        id={props.element.id}
        locked={attributes.locked}
        x={attributes.x}
        y={attributes.y}
        width={attributes.width}
        visible={visible}
        opacity={attributes.opacity}
        rotation={attributes.rotation ?? 0}
        onMouseDown={(e) => {
          props.onSelect();
        }}
        onMouseOver={(e) => {
          const locked = props.isSelectorActive && attributes.locked;
          if (!locked) props.onOver(true);
        }}
        onMouseOut={(e) => {
          props.onOver(false);
        }}
        scaleX={1}
        scaleY={1}
        ref={textRef}
        listening={!attributes.hidden}
        draggable={
          !attributes.locked &&
          !props.isSelectDescribe &&
          !props.isMultipleSelection &&
          !props.isDragging &&
          props.editMode
        }
        onTransform={(e) => {
          const node = textRef.current;
          const activeAnchor = trRef.current.getActiveAnchor();
          const newWidth = Math.max(node.width() * node.scaleX(), 20);
          const newHeight = Math.max(node.height() * node.scaleY(), 20);
          let rotation = node.rotation() ?? 0;
          if (
            activeAnchor === "middle-left" ||
            activeAnchor == "middle-right"
          ) {
            node.width(newWidth);
            node?.scaleX(1);
            node?.scaleY(1);
            let resizeValues: IResizeValues = {
              x: node.x(),
              y: node.y(),
              width: newWidth,
              height: newHeight,
              rotation: rotation ? rotation : 0,
            };
            props.onResize(resizeValues, true);
          }
          if (activeAnchor === "rotater") {
            let stageX = trRef.current.getStage()?.x();
            let stageY = trRef.current.getStage()?.y();
            let container = trRef.current
              .getStage()
              ?.container() as HTMLDivElement;
            let offsetX = container.offsetLeft + (stageX ?? 0);
            let offsetY = container.offsetTop + (stageY ?? 0);
            props.onRotation(
              rotation,
              node.x() - 50,
              node.y() - 50,
              offsetX,
              offsetY
            );
          }
        }}
        onTransformEnd={(e) => {
          const node = textRef.current;
          const scaleX = node!.scaleX();
          const scaleY = node!.scaleY();
          node?.scaleX(1);
          node?.scaleY(1);
          const activeAnchor = trRef.current.getActiveAnchor();
          let newWidth = Math.max(5, node!.width() * scaleX);
          let newHeight = Math.max(node!.height() * scaleY);
          let factor = undefined;
          if (
            !(activeAnchor === "middle-left" || activeAnchor == "middle-right")
          ) {
            factor = newWidth / node.width();
          }
          let rotation = node.rotation() ?? 0;
          let resizeValues: IResizeValues = {
            x: node.x(),
            y: node.y(),
            width: newWidth,
            height: newHeight,
            factor: factor,
            rotation: rotation ? rotation : 0,
          };
          props.onResize(resizeValues);
          props.onRotation();
        }}
        onDragMove={() => {
          props.onDrag(textRef, trRef);
        }}
        onDragEnd={(e) => {
          const node = textRef.current;
          let rotation = node.rotation() ?? 0;
          let resizeValues: IResizeValues = {
            x: node.x(),
            y: node.y(),
            width: node.width(),
            height: node.height(),
            rotation: rotation ? rotation : 0,
          };
          props.onDragEnd(textRef, trRef, resizeValues);
        }}
        onDragStart={(e) => {
          if (e.evt.shiftKey) textRef.current.stopDrag();
        }}
        onDblClick={(e) => {
          // Double Click to edit mode
          const node = textRef.current;
          if (
            !props.isSelectDescribe &&
            trRef.current &&
            !e.evt.shiftKey &&
            attributes.locked !== true
          ) {
            const node = textRef.current;
            let stageX = trRef.current.getStage()?.x();
            let stageY = trRef.current.getStage()?.y();
            let container = trRef.current
              .getStage()
              ?.container() as HTMLDivElement;
            let offsetX = container.offsetLeft + (stageX ?? 0);
            let offsetY = container.offsetTop + (stageY ?? 0);
            let height = node.height();
            props.onEdit(true, offsetX, offsetY, height);
          }
        }}
      >
        {(props.element.attributes as ITextAttributes).partsForRendering.map(
          (elementToRender, index) => {
            return (
              <TextSegmentElement
                key={index}
                element={props.element}
                segment={elementToRender}
                containerId={props.containerId}
                segmentIndex={index}
              />
            );
          }
        )}
        <Rect
          key={props.element.id + "-rect"}
          id={props.element.id + "-rect"}
          ref={rectRef}
          listening={!attributes.hidden}
          fill={"transparent"}
          width={attributes.width}
        ></Rect>
        <Shape
          listening={!attributes.hidden}
          key={props.element.id + "-clickShape"}
          id={props.element.id + "-clickShape"}
          x={0}
          y={0}
          height={attributes.height}
          width={attributes.width}
          sceneFunc={(context, shape) => {
            context.rect(0, 0, shape.getAttr("width"), shape.getAttr("height"));
            // automatically fill shape from props and draw hit region
            context.fillStrokeShape(shape);
          }}
        />
      </Group>
      {(props.isSelected || props.isOver || showDescribedColor) && (
        <Transformer
          ref={trRef}
          listening={!attributes.hidden}
          keepRatio={true}
          visible={visible}
          borderStroke={getTransformerBorderColor()}
          borderStrokeWidth={props.isOver || showDescribedColor ? 2 : 1}
          rotateEnabled={
            attributes.locked !== true &&
            props.isSelected &&
            !props.isMultipleSelection &&
            !props.isSelectDescribe
          }
          resizeEnabled={
            props.isSelected &&
            !props.isMultipleSelection &&
            !props.isSelectDescribe
          }
          rotateAnchorOffset={20}
          rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
          enabledAnchors={
            attributes.locked
              ? []
              : [
                  "top-left",
                  "middle-left",
                  "bottom-left",
                  "top-right",
                  "middle-right",
                  "bottom-right",
                ]
          }
          boundBoxFunc={(oldBox, newBox) => {
            // limit resize
            if (newBox.width < 5 || newBox.height < 5) {
              return oldBox;
            }
            return newBox;
          }}
        >
          <ElementLockedAddIn ref={lockIconRef} />
          <CornersAddIn
            show={props.isSelected && props.isSelectDescribe}
            width={attributes.width ?? 0}
            height={attributes.height ?? 0}
            scaleX={
              !textRef.current
                ? 1
                : textRef.current.getLayer()?.getStage().scaleX() ?? 1
            }
            scaleY={
              !textRef.current
                ? 1
                : textRef.current.getLayer()?.getStage().scaleY() ?? 1
            }
          />
        </Transformer>
      )}
    </>
  );
};

export default TextCanvasElement;
